import type { ActiveAgentEffect } from './agentEffectsStore'

/**
 * Build a single, coalesced screen-reader announcement for the currently-active
 * agent effects, e.g. "Xavior created 1 event, updated 2 tasks". Returns '' when
 * there is nothing to announce. Pure — the live-region component feeds it
 * `store.list()` and renders the result into one polite aria-live region so
 * bursts collapse into one utterance instead of per-row spam. It deliberately
 * announces type + count only (never titles/bodies) to avoid leaking content.
 */

const KIND_ORDER: ReadonlyArray<ActiveAgentEffect['kind']> = [
  'created', 'updated', 'moved', 'deleted', 'restored', 'submitted', 'read',
]

function pluralize(type: string, count: number): string {
  return count === 1 ? type : `${type}s`
}

export function summarizeAgentEffects(effects: ActiveAgentEffect[]): string {
  if (effects.length === 0) return ''

  const byKind = new Map<string, Map<string, number>>()
  for (const e of effects) {
    let types = byKind.get(e.kind)
    if (!types) { types = new Map(); byKind.set(e.kind, types) }
    types.set(e.resourceType, (types.get(e.resourceType) ?? 0) + 1)
  }

  const segments: string[] = []
  for (const kind of KIND_ORDER) {
    const types = byKind.get(kind)
    if (!types) continue
    for (const [type, count] of types) {
      segments.push(`${kind} ${count} ${pluralize(type, count)}`)
    }
  }

  return segments.length ? `Xavior ${segments.join(', ')}` : ''
}
